// SIMPLIFIED INSIGHTS CONTAINER
// Focus on 4 core metrics: card views, link taps, contact downloads, leads generated
// Complex analytics features have been commented out but can be restored if needed

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useAuth } from 'contexts/AuthContext';
import insightsSocket from 'sockets/insightsSocketService';
import {
  fetchInsightsData,
  fetchLeadsInsights,
  fetchViewsInsights,
  fetchLinkTapsInsights,
  // RESTORED: Recent Activity API
  fetchRecentActivity
} from 'apis/insightsApi';

// COMMENTED OUT: Complex accessory tracking - can be restored if needed
/*
// Import accessory utilities
import { 
  extractAccessoryScanEvents,
  buildCardAccessoryMapping
} from 'components/insights/accessory/AccessoryUtils';
// Import the accessory APIs
import { getUserAccessories } from 'apis/accessoryApi';
*/

// SIMPLIFIED: Core insight components only - Updated to use correct Views Over Time
import ViewsOverTime from '../components/insights/charts/ViewsOverTime';
import LeadsOverTime from '../components/insights/charts/LeadsOverTimeChartJS';
import StatsGrid from '../components/insights/stats/StatsGrid';
import InsightsMenu from '../components/insights/menu/InsightsMenu';

// RESTORED: Recent Activity and Link Taps components
import RecentActivity from '../components/insights/activity/RecentActivity';
import LinkTapsView from '../components/insights/linkTaps/LinkTapsView';
// import LinkTapsTable from '../components/insights/linkTaps/LinkTapsTable'; // Unused - commented out

// ADDED: Calendar filter component (currently unused but available for future use)
// import CalendarFilter from '../components/common/calendar/CalendarFilter';

// COMMENTED OUT: Other complex analytics components - can be restored if needed
// import ViewsByAccessory from '../components/insights/accessory/ViewsByAccessory';
// import ViewsByAccessoryDetail from '../components/insights/accessory/ViewsByAccessoryDetail';
import ViewTypeBreakdown from '../components/insights/breakdown/ViewTypeBreakdownChartJS';
import LeadTypeBreakdown from '../components/insights/breakdown/LeadTypeBreakdownChartJS';
import LinkTapsChart from '../components/insights/charts/LinkTapsChartJS';

// Unique identifiers for event listeners to ensure precise removal
const LISTENER_IDS = {
  STATS_UPDATE: 'insights-stats-update',
  LEAD_CAPTURED: 'insights-lead-captured',
  CARD_VIEW: 'insights-card-view',
  LINK_TAP: 'insights-link-tap',
  CONNECT: 'insights-socket-connect',
  DISCONNECT: 'insights-socket-disconnect'
};

const InsightsContainer = () => {
  const { authState } = useAuth();
  const [currentView, setCurrentView] = useState('main');

  // SIMPLIFIED: Core data states only (4 metrics)
  const [insightsData, setInsightsData] = useState(null);
  const [leadsData, setLeadsData] = useState(null);
  const [viewsData, setViewsData] = useState(null);
  const [linkTapsData, setLinkTapsData] = useState(null);
  // RESTORED: Recent Activity data state - with proper view display
  const [recentActivity, setRecentActivity] = useState([]);

  // Ensure card views always show in recent activity
  useEffect(() => {
    if (viewsData && viewsData.overTime && viewsData.overTime.length > 0) {
      // Get the 3 most recent card views
      const recentViews = viewsData.overTime
        .slice(-5) // Get the last 5 views
        .map(view => ({
          type: 'card_view',
          user: view.metadata?.userName || view.user || 'Someone',
          action: view.metadata?.cardHolderName ?
            `viewed ${view.metadata.cardHolderName}'s card` :
            'viewed your card',
          timestamp: view.timestamp || new Date().toISOString(),
          sourceType: view.sourceType || view.source || 'share_link',
          metadata: {
            ...view.metadata,
            sourceType: view.sourceType || view.source || 'share_link'
          }
        }));

      // Add these to recent activity if they don't already exist
      if (recentViews.length > 0) {
        console.log('Adding card views to recent activity:', recentViews);
        setRecentActivity(prev => {
          // Filter out duplicates by checking timestamps
          const existingTimestamps = new Set(prev.map(a => a.timestamp));
          const newViews = recentViews.filter(v => !existingTimestamps.has(v.timestamp));
          return [...newViews, ...prev].slice(0, 15); // Keep the list to 15 items max
        });
      }
    }
  }, [viewsData]);

  // ADDED: Date range state for calendar filter
  const [dateRange, setDateRange] = useState([
    new Date('2024-01-01'),
    new Date()
  ]);

  // COMMENTED OUT: Other complex tracking features - can be restored if needed
  // const [userAccessories, setUserAccessories] = useState([]);
  // const [accessoryScanData, setAccessoryScanData] = useState([]);

  // State management
  const [loading, setLoading] = useState(true);
  const [socketConnected, setSocketConnected] = useState(false);
  const [error, setError] = useState(null);

  // Refs to keep track of component lifecycle and prevent race conditions
  const isMounted = useRef(true);

  // Get userId from auth context
  const userId = authState?.user?.userId;

  // FIXED: Basic views data processing - avoid over-processing backend data
  const processedViewsData = useCallback(() => {
    if (!viewsData) return { byType: {}, overTime: [] };

    // Enhanced debugging
    console.log("Raw viewsData received:", {
      hasData: !!viewsData,
      byTypeExists: !!viewsData.byType,
      byTypeType: typeof viewsData.byType,
      byTypeKeys: viewsData.byType ? Object.keys(viewsData.byType) : [],
      byTypeValues: viewsData.byType ? Object.values(viewsData.byType) : [],
      totalViews: viewsData.total,
      fullByType: viewsData.byType,
      sampleOverTimeEvents: viewsData.overTime ? viewsData.overTime.slice(0, 3) : []
    });

    // CRITICAL FIX: Use backend data as-is, since backend already normalizes source types correctly
    const byType = {
      accessory: 0,
      qr_code: 0,
      share_link: 0
    };

    // Only use the backend's categorized data without additional processing
    if (viewsData.byType) {
      byType.accessory = typeof viewsData.byType.accessory === 'number' ? viewsData.byType.accessory : 0;
      byType.qr_code = typeof viewsData.byType.qr_code === 'number' ? viewsData.byType.qr_code : 0;
      byType.share_link = typeof viewsData.byType.share_link === 'number' ? viewsData.byType.share_link : 0;

      console.log("🔧 USING BACKEND CATEGORIZED DATA:", {
        accessory: byType.accessory,
        qr_code: byType.qr_code,
        share_link: byType.share_link,
        note: "No frontend normalization - backend handles this correctly"
      });
    }

    // Keep overTime data as-is since backend should provide correctly formatted data
    const overTime = Array.isArray(viewsData.overTime) ? viewsData.overTime : [];

    console.log("Processed views data (minimal processing):", { byType, overTime });

    return {
      ...viewsData,
      byType,
      overTime
    };
  }, [viewsData]);
  // FIXED: Handle date range changes from calendar filter - simplified to prevent circular dependencies
  const handleDateRangeChange = useCallback((startDate, endDate) => {
    console.log('Date range changed:', { startDate, endDate });
    setDateRange([startDate, endDate]);
    // Note: Data refetch will be handled by separate useEffect listening to dateRange changes
  }, []);
  // ADDED: Fetch data with specific date range - REMOVED: Merged into handleDateRangeChange to fix circular dependency

  // COMMENTED OUT: Complex accessory processing - can be restored if needed
  /*
  useEffect(() => {
    const fetchUserAccessories = async () => {
      if (!authState?.user?.userId) return;
      
      try {
        console.log("Fetching user accessories for insights...");
        const response = await getUserAccessories(authState.user.userId);
        
        if (response && response.accessories) {
          console.log("Found accessories:", response.accessories.length);
          setUserAccessories(response.accessories);
        }
      } catch (error) {
        console.error("Error fetching user accessories:", error);
      }
    };
    
    fetchUserAccessories();
  }, [authState?.user?.userId]);

  // Enhanced accessory extraction with full data reconciliation
  useEffect(() => {
    if (!insightsData) return;
    
    // Create enhanced insights data that includes all necessary info for reconciliation
    const enhancedInsightsData = {
      ...insightsData,
      // Include viewsData for reconciliation
      viewsData: viewsData || insightsData.viewsData,
      // Include viewTypeDistribution for reconciliation
      viewTypeDistribution: insightsData.viewTypeDistribution,
      // Add the real accessory data to the insights data
      accessories: userAccessories
    };
    
    // Use a clean extraction process to avoid duplicates
    const accessoryEvents = extractAccessoryScanEvents(enhancedInsightsData);
    
    // Debug for accessory scan counts
    console.log('EXTRACTED ACCESSORY EVENTS:', {
      count: accessoryEvents.length,
      sample: accessoryEvents.slice(0, 3)
    });
    
    setAccessoryScanData(accessoryEvents);
  }, [insightsData, userAccessories, viewsData]);
  */  // FIXED: Sequential data fetching with proper timeout cleanup to prevent freezing
  const fetchInitialData = useCallback(async () => {
    // Prevent fetching if component is unmounted
    if (!isMounted.current) return;

    if (!userId) return;

    console.log('🔄 Starting data fetch for insights page');
    setLoading(true);
    setError(null);

    // CRITICAL FIX: Store timeout ID for proper cleanup
    let timeoutId = null;
    const timeoutPromise = new Promise((_, reject) => {
      timeoutId = setTimeout(() => {
        console.warn('⚠️ Data fetch timeout triggered after 30 seconds');
        reject(new Error('Data fetch timeout after 30 seconds'));
      }, 30000);
    });

    try {
      // Create date range object for API calls
      const dateRangeParam = {
        startDate: dateRange[0],
        endDate: dateRange[1]
      };

      // PERFORMANCE OPTIMIZATION: Load all data in parallel with timeout
      console.log('🚀 Loading all insights data with date range:', dateRangeParam);
      const [
        insightsResponse,
        leadsResponse,
        viewsResponse,
        linkTapsResponse,
        activityResponse
      ] = await Promise.race([
        Promise.all([
          fetchInsightsData(userId, dateRangeParam),
          fetchLeadsInsights(userId, dateRangeParam),
          fetchViewsInsights(userId, dateRangeParam),
          fetchLinkTapsInsights(userId, dateRangeParam),
          fetchRecentActivity(userId, dateRangeParam)
        ]),
        timeoutPromise
      ]);

      // Clear timeout on successful completion
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }

      // Check if component is still mounted before updating state
      if (!isMounted.current) {
        console.log('Component unmounted during data fetch, aborting state updates');
        return;
      }

      // Update all state at once to prevent multiple re-renders
      setInsightsData(insightsResponse.success && insightsResponse.data ? insightsResponse.data : { stats: {} });
      setLeadsData(leadsResponse.success && leadsResponse.data ? leadsResponse.data : { bySource: {}, overTime: [] });
      setViewsData(viewsResponse.success && viewsResponse.data ? viewsResponse.data : { byType: {}, overTime: [] });
      setLinkTapsData(linkTapsResponse.success && linkTapsResponse.data ? linkTapsResponse.data : { linkTaps: [], overTime: [] });
      setRecentActivity(activityResponse.success && activityResponse.data ? activityResponse.data : []);

      console.log('✅ All insights data loaded successfully');

    } catch (fetchError) {
      // Clear timeout on error
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }

      // Prevent updating state if component is unmounted
      if (!isMounted.current) {
        console.log('Component unmounted during fetch error handling, aborting');
        return;
      }

      console.error('❌ Error fetching insights data:', fetchError);

      // CRITICAL FIX: Provide fallback data on timeout/error to prevent infinite loading
      if (fetchError.message?.includes('timeout')) {
        console.warn('⚠️ Data fetch timed out, providing fallback data');
        setInsightsData({ stats: {} });
        setLeadsData({ bySource: {}, overTime: [] });
        setViewsData({ byType: {}, overTime: [] });
        setLinkTapsData({ linkTaps: [], overTime: [] });
        setRecentActivity([]);
      }

      setError(fetchError);
    } finally {
      // Prevent updating state if component is unmounted
      if (!isMounted.current) {
        console.log('Component unmounted during fetch completion, aborting state updates');
        return;
      }

      // CRITICAL FIX: Always stop loading, even on timeout/error
      setLoading(false);
    }
  }, [userId, dateRange]); // FIXED: Include dateRange in dependencies for proper refetching

  // Memoized listener functions to ensure consistent reference
  const statsUpdateListener = useCallback((data) => {
    if (!isMounted.current) return;
    try {
      setInsightsData(prev => {
        // Handle case where prev is null
        const prevStats = prev?.stats || {};
        return {
          ...prev,
          stats: {
            ...prevStats,
            leadsGenerated: {
              value: data.leadsGenerated || 0,
              percentage: prevStats.leadsGenerated?.percentage || 0,
              trend: prevStats.leadsGenerated?.trend || 'neutral'
            },
            cardViews: {
              value: data.cardViews || 0,
              percentage: prevStats.cardViews?.percentage || 0,
              trend: prevStats.cardViews?.trend || 'neutral'
            },
            linkTaps: {
              value: data.linkTaps || 0,
              percentage: prevStats.linkTaps?.percentage || 0,
              trend: prevStats.linkTaps?.trend || 'neutral'
            }
          }
        };
      });
    } catch (updateError) {
      console.error('Stats update error:', updateError);
    }
  }, []);
  const leadCapturedListener = useCallback((data) => {
    console.group('🔍 LEAD CAPTURED EVENT - INSIGHTS CONTAINER');
    console.log('1. Raw socket data:', data);
    console.log('2. Metadata keys:', Object.keys(data.metadata || {}));

    // Create new activity object with proper structure
    const newActivity = {
      type: 'lead_capture',
      actorName: data.leadName || data.name || data.user,
      action: data.action || 'connected with a card',
      timestamp: data.timestamp || new Date().toISOString(),
      metadata: {
        ...data.metadata,
        cardHolderName: data.metadata?.cardHolderName || data.metadata?.cardOwnerName || data.metadata?.cardName,
        leadName: data.leadName || data.name || data.user,
        leadEmail: data.leadEmail,
        cardId: data.cardId
      }
    };

    console.log('3. Processed activity:', newActivity);
    console.log('4. Card holder name:', newActivity.metadata.cardHolderName);
    console.groupEnd();

    // Add to recent activity
    setRecentActivity(prev => {
      // Filter out duplicates by checking timestamps
      const existingTimestamps = new Set(prev.map(a => a.timestamp));
      if (existingTimestamps.has(newActivity.timestamp)) {
        return prev;
      }
      return [newActivity, ...prev].slice(0, 15); // Keep the list to 15 items max
    });

    // Update leads data for the chart
    setLeadsData(prev => {
      if (!prev) return prev;
      const newData = [...prev];
      const today = new Date().toISOString().split('T')[0];
      const todayIndex = newData.findIndex(item => item.date === today);

      if (todayIndex >= 0) {
        const leadSource = data.source || data.leadSource || 'lead-capture-form';
        const update = {
          ...newData[todayIndex],
          total: (newData[todayIndex].total || 0) + 1
        };

        // Update the correct lead source count
        switch (leadSource) {
          case 'lead-capture-form':
            update.leadCaptureForm = (newData[todayIndex].leadCaptureForm || 0) + 1;
            break;
          case 'csv-import':
            update.csv = (newData[todayIndex].csv || 0) + 1;
            break;
          case 'manually-added':
            update.manuallyAdded = (newData[todayIndex].manuallyAdded || 0) + 1;
            break;
          default:
            // For any other source, increment the total only
            break;
        }

        newData[todayIndex] = update;
      }
      return newData;
    });
  }, []);
  const cardViewListener = useCallback((view) => {
    if (!isMounted.current) return;
    console.log('🔍 Card view received via socket:', view);

    // Extract and normalize sourceType - moved outside setViewsData for proper scope
    let sourceType = view.source || view.sourceType || view.viewSource || 'share_link';
    // Normalize sourceType values
    if (sourceType === 'nfc' || sourceType === 'tag') {
      sourceType = 'accessory';
    } else if (sourceType === 'website') {
      sourceType = 'share_link';
    }

    console.log('📊 Adding view with sourceType:', sourceType);

    setViewsData(prev => {
      // Handle case where prev is null
      const prevData = prev || { total: 0, byType: {}, overTime: [] };

      const updatedByType = {
        ...(prevData.byType || {}),
        [sourceType]: ((prevData.byType || {})[sourceType] || 0) + 1
      };

      return {
        ...prevData,
        total: (prevData.total || 0) + 1,
        byType: updatedByType,
        overTime: [...(prevData.overTime || []), {
          ...view,
          sourceType: sourceType
        }]
      };
    });    // RESTORED: Recent Activity tracking for card views
    const newActivity = {
      type: 'card_view',
      user: view.metadata?.userName || view.user || 'Someone',
      action: view.metadata?.cardHolderName ?
        `viewed ${view.metadata.cardHolderName}'s card` :
        'viewed your card',
      timestamp: view.timestamp || new Date().toISOString(),
      metadata: {
        cardHolderName: view.metadata?.cardHolderName,
        cardHolderPhoto: view.metadata?.cardHolderPhoto,
        profilePictureUrl: view.metadata?.profilePictureUrl || view.metadata?.viewerPhoto,
        userName: view.metadata?.userName,
        sourceType: sourceType, // Use the normalized sourceType here
        viewSource: sourceType, // Add duplicate for compatibility
        source: sourceType,     // Add duplicate for compatibility
        ...view.metadata
      }
    }; console.log('📊 Adding card view to recent activity:', newActivity);
    setRecentActivity(prev => [newActivity, ...(prev || []).slice(0, 4)]);
  }, []);  // Enhanced link tap listener with better duplicate detection and logging
  const linkTapListener = useCallback((tap) => {
    if (!isMounted.current) return;

    console.group('💡 Link Tap Event Processing - COMPREHENSIVE DEBUG');
    console.log('🔍 RAW tap event received:', tap);
    console.log('🔍 Tap event keys:', Object.keys(tap || {}));
    console.log('🔍 Tap metadata:', tap?.metadata);
    console.log('🔍 Tap metadata keys:', Object.keys(tap?.metadata || {}));
    console.log('🔍 User identification data:', {
      directUser: tap?.user,
      metadataUser: tap?.metadata?.user,
      metadataUserName: tap?.metadata?.userName,
      metadataViewerName: tap?.metadata?.viewerName,
      metadataTapperName: tap?.metadata?.tapperName,
      metadataName: tap?.metadata?.name,
      metadataDisplayName: tap?.metadata?.displayName,
      metadataEmail: tap?.metadata?.email,
      metadataViewerEmail: tap?.metadata?.viewerEmail,
      metadataTapperEmail: tap?.metadata?.tapperEmail,
      metadataProfilePictureUrl: tap?.metadata?.profilePictureUrl,
      metadataUserPhoto: tap?.metadata?.userPhoto,
      metadataViewerPhoto: tap?.metadata?.viewerPhoto,
      metadataTapperPhoto: tap?.metadata?.tapperPhoto
    });

    setLinkTapsData(prev => {
      const prevData = prev || { total: 0, linkTaps: [], overTime: [] };
      console.log('Current total:', prevData.total);

      // Enhanced duplicate detection with fuzzy timestamp matching
      // This helps avoid double-counting the same tap event
      const isDuplicate = prevData.overTime.some(existingTap => {
        // If exact timestamp match, likely a duplicate
        if (existingTap.timestamp === tap.timestamp) return true;

        // If timestamps are close (within 2 seconds) and other properties match
        const existingTime = new Date(existingTap.timestamp).getTime();
        const newTime = new Date(tap.timestamp).getTime();
        const timeCloseness = Math.abs(existingTime - newTime);

        if (timeCloseness < 2000) { // Within 2 seconds
          // Check if the same link was tapped
          const linkIdMatch = existingTap.linkId === tap.linkId;
          const linkTypeMatch = existingTap.linkType === tap.linkType;
          const cardIdMatch = existingTap.cardId === tap.cardId;

          if ((linkIdMatch || linkTypeMatch) && cardIdMatch) {
            console.log('Found duplicate tap based on time proximity and link matching');
            return true;
          }
        }

        return false;
      });

      if (isDuplicate) {
        console.log('🔄 Duplicate tap event - not incrementing count');
        console.groupEnd();
        return prevData;
      }

      console.log('✨ Valid new tap event - incrementing count from', prevData.total);
      console.groupEnd();

      // Get the card holder name from metadata
      const cardHolderName = tap.metadata?.cardHolderName;

      // Normalize the new tap event format for storage
      const normalizedTap = {
        linkId: tap.linkId || `link-${Date.now()}`,
        linkType: tap.linkType || 'link',
        linkUrl: tap.linkUrl || '',
        linkTitle: tap.linkType || 'Link',
        cardId: tap.cardId,
        timestamp: tap.timestamp || new Date().toISOString(),
        metadata: {
          ...tap.metadata,
          cardHolderName
        }
      };

      // Update the linkTapsData state with the new tap
      return {
        ...prevData,
        total: (prevData.total || 0) + 1,
        linkTaps: [...(prevData.linkTaps || []), normalizedTap],
        overTime: [...(prevData.overTime || []), normalizedTap]
      };
    });

    // Also update the main stats to keep counts consistent
    setInsightsData(prev => {
      if (!prev) return prev;

      const prevStats = prev.stats || {};
      const currentTaps = typeof prevStats.linkTaps === 'object'
        ? (prevStats.linkTaps?.value || 0)
        : (typeof prevStats.linkTaps === 'number' ? prevStats.linkTaps : 0);

      console.log('Updating main stats link taps count:', {
        before: currentTaps,
        after: currentTaps + 1
      });

      return {
        ...prev,
        stats: {
          ...prevStats,
          linkTaps: {
            value: currentTaps + 1,
            percentage: prevStats.linkTaps?.percentage || 0,
            trend: prevStats.linkTaps?.trend || 'neutral'
          }
        }
      };
    });    // Update recent activity with enhanced naming and comprehensive debugging
    console.group('🎯 Recent Activity Creation for Link Tap');

    // Determine the best available user name with priority fallback
    const possibleUserNames = {
      metadataUserName: tap.metadata?.userName,
      metadataViewerName: tap.metadata?.viewerName,
      metadataTapperName: tap.metadata?.tapperName,
      metadataName: tap.metadata?.name,
      metadataDisplayName: tap.metadata?.displayName,
      directUser: tap.user,
      metadataUser: tap.metadata?.user
    };

    console.log('🔍 All possible user name sources:', possibleUserNames);

    // Find first non-empty user name
    const bestUserName = Object.entries(possibleUserNames).find(([key, value]) => {
      const isValid = value && typeof value === 'string' && value.trim() !== '';
      console.log(`🔍 Checking ${key}: "${value}" -> ${isValid ? 'VALID' : 'INVALID'}`);
      return isValid;
    });

    const finalUserName = bestUserName ? bestUserName[1] : 'Someone';
    console.log('🎯 Final selected user name:', finalUserName, 'from source:', bestUserName ? bestUserName[0] : 'fallback');

    // Try to extract user name from email if no direct name available
    let userNameFromEmail = null;
    const emailSources = [
      tap.metadata?.email,
      tap.metadata?.viewerEmail,
      tap.metadata?.tapperEmail,
      tap.metadata?.userEmail
    ];

    console.log('📧 Email sources to check:', emailSources);

    for (const email of emailSources) {
      if (email && typeof email === 'string' && email.includes('@')) {
        userNameFromEmail = email.split('@')[0];
        console.log(`📧 Extracted name from email "${email}": "${userNameFromEmail}"`);
        break;
      }
    }

    const activityUserName = finalUserName !== 'Someone' ? finalUserName : (userNameFromEmail || 'Someone');
    console.log('🎯 FINAL Activity user name:', activityUserName);

    const newActivity = {
      type: 'link_tap',
      user: activityUserName,
      action: tap.metadata?.cardHolderName
        ? `tapped ${tap.metadata.cardHolderName}'s ${tap.linkType || 'link'}`
        : `tapped ${tap.linkType || 'link'}`,
      timestamp: tap.timestamp || new Date().toISOString(),
      metadata: {
        linkType: tap.linkType,
        linkUrl: tap.linkUrl,
        linkTitle: tap.linkTitle,
        cardId: tap.cardId,
        cardHolderName: tap.metadata?.cardHolderName,
        profilePictureUrl: tap.metadata?.profilePictureUrl || tap.metadata?.userPhoto,
        userName: tap.metadata?.userName,
        // Preserve all original metadata for debugging
        originalTapData: tap,
        userNameSources: possibleUserNames,
        selectedUserNameSource: bestUserName ? bestUserName[0] : 'fallback',
        emailExtracted: userNameFromEmail,
        ...tap.metadata
      }
    };

    console.log('🎯 Created activity object:', newActivity);
    console.groupEnd();

    setRecentActivity(prev => [newActivity, ...(prev || []).slice(0, 4)]);
  }, []);
  // Enhanced contact download listener with better debugging
  const contactDownloadListener = useCallback((download) => {
    if (!isMounted.current) return;

    console.log('Contact download event received:', download);

    setInsightsData(prev => {
      // Handle case where prev is null
      const prevStats = prev?.stats || {};

      // Explicitly log current value before update
      const currentValue = typeof prevStats.contactsDownloaded === 'object'
        ? (prevStats.contactsDownloaded?.value || 0)
        : (typeof prevStats.contactsDownloaded === 'number' ? prevStats.contactsDownloaded : 0);

      console.log('UPDATING DOWNLOADS COUNT:', {
        before: currentValue,
        after: currentValue + 1
      });

      // Create the updated stats object with consistent structure
      const updatedContactsDownloaded = {
        value: currentValue + 1,
        percentage: prevStats.contactsDownloaded?.percentage || 0,
        trend: prevStats.contactsDownloaded?.trend || 'neutral'
      };

      // Return the updated state
      return {
        ...prev,
        stats: {
          ...prevStats,
          contactsDownloaded: updatedContactsDownloaded
        }
      };
    });    // RESTORED: Recent Activity tracking for contact downloads
    const newActivity = {
      type: 'contact_download',
      user: download.user || 'Someone',
      action: download.metadata?.cardHolderName ? `downloaded ${download.metadata.cardHolderName}'s contact` : 'downloaded your contact',
      timestamp: download.timestamp || new Date().toISOString(),
      metadata: {
        cardHolderName: download.metadata?.cardHolderName,
        cardHolderPhoto: download.metadata?.cardHolderPhoto,
        ...download.metadata
      }
    };

    setRecentActivity(prev => [newActivity, ...(prev || []).slice(0, 4)]);
  }, []);  // FIXED: Socket connection management - simplified to prevent race conditions
  useEffect(() => {
    // Reset mounted state
    isMounted.current = true;

    if (!userId) {
      console.log('No userId available, skipping socket connection');
      setLoading(false);
      return;
    }

    console.log('🔄 InsightsContainer mounted, initializing...');

    // Initialize state
    setSocketConnected(false);
    setError(null);
    setLoading(true);

    // Fetch initial data
    fetchInitialData()
      .catch(error => {
        if (!isMounted.current) return;
        console.error("❌ Error in initial data fetch:", error);
        if (error.message !== 'Component unmounted') {
          setError(error);
          setLoading(false);
        }
      });

    // Cleanup function
    return () => {
      console.log('🧹 Cleaning up InsightsContainer...');
      isMounted.current = false;

      try {
        setSocketConnected(false);
      } catch (err) {
        console.warn('Failed to update socketConnected state during cleanup');
      }

      // Disconnect socket and remove listeners
      try {
        insightsSocket.disconnect();
      } catch (cleanupError) {
        console.error('Error during socket cleanup:', cleanupError);
      }

      console.log('✅ InsightsContainer cleanup complete');
    };
  }, [userId, fetchInitialData]);

  // FIXED: Separate effect for socket connection after data loads
  useEffect(() => {
    if (!userId || !insightsData || loading) {
      return; // Don't connect socket until data is loaded
    }

    console.log('🔌 Setting up socket connection...');

    // Connect socket
    insightsSocket.connect(userId);
    setSocketConnected(true);

    // Register event listeners
    insightsSocket
      .onStatsUpdate(statsUpdateListener)
      .onLeadCaptured(leadCapturedListener)
      .onCardView(cardViewListener)
      .onLinkTap(linkTapListener)
      .onContactDownload(contactDownloadListener);

    // Connection status handlers
    const connectHandler = () => {
      if (isMounted.current) setSocketConnected(true);
    };
    const disconnectHandler = () => {
      if (isMounted.current) setSocketConnected(false);
    };

    insightsSocket
      .on('connect', connectHandler)
      .on('disconnect', disconnectHandler);

    // Enhanced share tracking
    insightsSocket.on('shareTracked', (shareData) => {
      if (!isMounted.current) return;

      console.log('📊 Share event received:', shareData);

      // Update main stats
      setInsightsData(prev => {
        if (!prev) return prev;
        const prevStats = prev.stats || {};
        const currentViews = typeof prevStats.cardViews === 'object'
          ? (prevStats.cardViews?.value || 0)
          : (typeof prevStats.cardViews === 'number' ? prevStats.cardViews : 0);

        return {
          ...prev,
          stats: {
            ...prevStats,
            cardViews: {
              value: currentViews + 1,
              percentage: prevStats.cardViews?.percentage || 0,
              trend: prevStats.cardViews?.trend || 'neutral'
            }
          }
        };
      });

      // Update views data
      setViewsData(prev => {
        if (!prev) return prev;
        const updatedByType = {
          accessory: Number(prev.byType?.accessory || 0),
          qr_code: Number(prev.byType?.qr_code || 0),
          share_link: Number(prev.byType?.share_link || 0) + 1
        };

        const shareEvent = {
          source: 'share_link',
          timestamp: shareData.timestamp || new Date().toISOString(),
          cardId: shareData.cardId,
          metadata: { ...(shareData.metadata || {}), shareType: shareData.shareType }
        };

        return {
          total: (prev.total || 0) + 1,
          byType: updatedByType,
          overTime: [...(prev.overTime || []), shareEvent]
        };
      });

      // Add to recent activity
      const newActivity = {
        type: 'share',
        user: shareData.user || 'Someone',
        action: 'shared a card',
        timestamp: shareData.timestamp || new Date().toISOString(),
        metadata: {
          shareType: shareData.shareType,
          ...shareData.metadata
        }
      };

      setRecentActivity(prev => [newActivity, ...(prev || []).slice(0, 14)]);
    });

    // Cleanup for this effect
    return () => {
      console.log('🧹 Cleaning up socket listeners...');
      try {
        insightsSocket
          .offStatsUpdate(statsUpdateListener)
          .offLeadCaptured(leadCapturedListener)
          .offCardView(cardViewListener)
          .offLinkTap(linkTapListener)
          .offContactDownload(contactDownloadListener)
          .off('connect', connectHandler)
          .off('disconnect', disconnectHandler)
          .off('shareTracked');
      } catch (err) {
        console.error('Error cleaning up socket listeners:', err);
      }
    };
  }, [userId, insightsData, loading, statsUpdateListener, leadCapturedListener, cardViewListener, linkTapListener, contactDownloadListener]);

  // FIXED: Simple date range effect to trigger data refetch
  useEffect(() => {
    // Only refetch if we already have data loaded (not initial load)
    if (userId && !loading && insightsData !== null) {
      console.log('📅 Date range changed, refetching data...');
      fetchInitialData();
    }
  }, [dateRange, userId, fetchInitialData]); // Simplified dependencies

  // Render loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Render error state
  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          textAlign: 'center'
        }}
      >
        <Typography variant="h6" color="error">
          Error Loading Insights
        </Typography>
        <Typography variant="body2">
          {error.message || 'An unexpected error occurred'}
        </Typography>
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{ mt: 2, cursor: 'pointer' }}
          onClick={fetchInitialData}
        >
          Retry Loading
        </Typography>
      </Box>
    );
  }

  // Get properly formatted views data
  const processedViews = processedViewsData();

  // RESTORED: Link Taps detailed view routing
  if (currentView === 'link-taps') {
    return <LinkTapsView
      data={linkTapsData?.linkTaps || []}
      totalTaps={linkTapsData?.total || 0}
      dateRange={dateRange}
      onDateRangeChange={handleDateRangeChange}
      onBack={() => setCurrentView('main')}
    />;
  }

  // COMMENTED OUT: Complex view routing - can be restored if needed
  /*
  if (currentView === 'accessory') {
    return <ViewsByAccessoryDetail 
      data={{
        accessories: userAccessories,
        viewsData: viewsData,
        viewTypeDistribution: insightsData?.viewTypeDistribution,
        stats: insightsData?.stats,
        viewsOverTimeData: insightsData?.viewsOverTimeData,
        accessoryScanData: accessoryScanData
      }}
      onBack={() => setCurrentView('main')} 
      loading={loading}
    />;
  }
  */

  // Create safe stats object to prevent null access
  const safeStats = {
    leadsGenerated: insightsData?.stats?.leadsGenerated || { value: 0, percentage: 0, trend: 'neutral' },
    linkTaps: insightsData?.stats?.linkTaps || { value: 0, percentage: 0, trend: 'neutral' },
    cardViews: insightsData?.stats?.cardViews || { value: 0, percentage: 0, trend: 'neutral' },
    contactsDownloaded: insightsData?.stats?.contactsDownloaded || { value: 0, percentage: 0, trend: 'neutral' }
  };  // SIMPLIFIED: Main insights view with core 4 metrics only
  return (
    <Box sx={{ padding: '20px' }}>
      <InsightsMenu
        onDateRangeChange={handleDateRangeChange}
        dateRange={dateRange}
      />

      <Box sx={{ marginTop: '24px' }}>
        <StatsGrid stats={safeStats} />
      </Box>      {/* SIMPLIFIED: Core charts section - Views Over Time and Recent Activity */}
      <Box sx={{
        display: 'flex',
        gap: '18px',
        marginTop: '24px',
        height: '414px'
      }}>        <Box sx={{ flex: 1 }}>
          <ViewsOverTime data={processedViews} />
        </Box>
        {/* RESTORED: Recent Activity component */}
        <Box sx={{ width: '338px' }}>
          <RecentActivity activities={recentActivity || []} />
        </Box>
      </Box>      {/* Leads Over Time chart paired with Lead Type Distribution */}
      <Box sx={{
        display: 'flex',
        gap: '18px',
        marginTop: '24px',
        height: '414px'
      }}>
        <Box sx={{ flex: 1 }}>
          <LeadsOverTime data={leadsData?.overTime || []} />
        </Box>
        <Box sx={{ width: '338px' }}>
          <LeadTypeBreakdown data={leadsData?.bySource || {}} />
        </Box>
      </Box>      {/* Link Taps Over Time chart paired with View Type Distribution */}
      <Box sx={{
        display: 'flex',
        gap: '18px',
        marginTop: '24px',
        height: '414px'
      }}>
        <Box sx={{ flex: 1 }}>
          <LinkTapsChart
            data={linkTapsData}
            onViewMore={() => setCurrentView('link-taps')}
          />
        </Box>        <Box sx={{ width: '338px' }}>
          <ViewTypeBreakdown
            key={`viewtype-${processedViews.byType.accessory}-${processedViews.byType.qr_code}-${processedViews.byType.share_link}-${Date.now()}`}
            data={processedViews.byType}
            /* Add debug props to help troubleshoot */
            rawViewsData={viewsData}
            processedData={processedViews}
          />        </Box>
      </Box>

      {/* COMMENTED OUT: Complex accessory analytics - can be restored if needed */}
      {/*
      <Box sx={{
        display: 'flex',
        gap: '18px',
        marginTop: '24px',
        height: '414px'
      }}>
        <Box sx={{ width: '338px' }}>
          <ViewsByAccessory 
            data={{
              accessories: userAccessories,
              viewsData: viewsData,
              viewTypeDistribution: insightsData?.viewTypeDistribution,
              stats: insightsData?.stats,
              viewsOverTimeData: insightsData?.viewsOverTimeData,
              accessoryScanData: accessoryScanData
            }}
            onViewMore={() => setCurrentView('accessory')} 
            loading={loading}
          />
        </Box>
      </Box>
      */}
    </Box>
  );
};

export default InsightsContainer;
